/**
 * voice-v8 — Karte „Zugangswerte ablegen“ (27.10.2026, Autonomie-Modus B9; Server: server/zugangswert-ablage.ts).
 *
 * Die Gruppe der vorgemerkten Zugangswerte (z. B. SMTP-Benutzer + SMTP-Passwort) als EINE Karte mit EINEM Knopf. Die Karte kennt nur
 * Namen und Herkunft (Rechnername der Seite) — die Werte hat nur der Server. „Ablegen“ schickt den festen Wortlaut aus Kennung und
 * Kurzwert des Servers (shared/zugangswert.ts), „Verwerfen“ den Verwerfen-Wortlaut. Nur ein ECHTER Tipp sendet (knopf-paar.ts,
 * `isTrusted`); der Server löst nur ein, wenn die Nachricht vom Knopf der Karte kommt. Alles als Text (textContent).
 */
import {
  ZUGANGSWERT_KENNUNG,
  ZUGANGSWERT_KURZWERT,
  ZUGANGSWERTE_HOECHSTENS,
  ZUGANGSWERTE_KARTE,
  ZUGANGSWERT_ZWECKE,
  zugangswerteKnopfJa,
  zugangswerteKnopfNein,
} from "../../../shared/zugangswert.js";
import { absatz, baueKarte, kartenTitel } from "../karte.js";
import { unwrapResult } from "./hilfe.js";
import { fristAus, haengeKnopfReiheAn } from "./knopf-paar.js";

const TITEL = "Zugangswerte ablegen";
const ABGELAUFEN_SATZ = "Diese Karte gilt nicht mehr (30 Minuten). Bitte Vespera, die Werte neu zu lesen.";
const NAME_FORM = /^[a-z0-9][a-z0-9._-]{1,59}$/;
const HOST_FORM = /^[a-z0-9.-]{1,253}$/;

interface Eintrag {
  name: string;
  herkunft: string;
  /** Fester Wortlaut aus der Liste des Servers (ZUGANGSWERT_ZWECKE) — sonst keiner. */
  zweck: string | null;
}

const ZWECK_TEXTE: ReadonlySet<string> = new Set(Object.values(ZUGANGSWERT_ZWECKE));

/** Die Namen aus dem Ergebnis — `null`, wenn einer nicht die erwartete Form hat oder es keine bzw. zu viele sind. */
function namenAus(v: unknown): Eintrag[] | null {
  if (!Array.isArray(v) || v.length === 0 || v.length > ZUGANGSWERTE_HOECHSTENS) return null;
  const aus: Eintrag[] = [];
  for (const x of v as unknown[]) {
    const { name, herkunft, zweck } = (x ?? {}) as { name?: unknown; herkunft?: unknown; zweck?: unknown };
    if (typeof name !== "string" || !NAME_FORM.test(name)) return null;
    aus.push({
      name,
      herkunft: typeof herkunft === "string" && HOST_FORM.test(herkunft) ? herkunft : "unbekannt",
      zweck: typeof zweck === "string" && ZWECK_TEXTE.has(zweck) ? zweck : null,
    });
  }
  return aus;
}

/** Ist dieses Ergebnis die Gruppen-Karte (vom Server gefüllt, Kennung, Kurzwert und Namen gültig)? */
export function istZugangswerteRueckfrage(tool: string, result: unknown): boolean {
  if (tool !== ZUGANGSWERTE_KARTE) return false;
  const r = unwrapResult(result);
  return (
    r.wartet_auf_bestaetigung === true &&
    typeof r.vorschlag_id === "string" &&
    ZUGANGSWERT_KENNUNG.test(r.vorschlag_id) &&
    typeof r.kurzwert === "string" &&
    ZUGANGSWERT_KURZWERT.test(r.kurzwert) &&
    namenAus(r.namen) !== null
  );
}

/** Die Karte — `null` ohne gültige Kennung, Kurzwert oder Namen. */
export function buildZugangswerteCard(result: unknown): HTMLElement | null {
  const r = unwrapResult(result) as { vorschlag_id?: unknown; kurzwert?: unknown; namen?: unknown; gueltig_bis_ms?: unknown };
  const id = typeof r.vorschlag_id === "string" ? r.vorschlag_id : "";
  const kurz = typeof r.kurzwert === "string" ? r.kurzwert : "";
  const namen = namenAus(r.namen);
  if (!ZUGANGSWERT_KENNUNG.test(id) || !ZUGANGSWERT_KURZWERT.test(kurz) || !namen) return null;
  const el = baueKarte("zugangswerte");
  el.appendChild(kartenTitel(TITEL));
  el.appendChild(absatz("Der Browser hat diese Werte direkt an Vespera gegeben. Vespera selbst sieht sie nicht."));
  const liste = document.createElement("ul");
  liste.className = "zugangswerte-liste";
  for (const n of namen) {
    const li = document.createElement("li");
    const name = document.createElement("span");
    name.className = "zugangswerte-name";
    name.textContent = n.name;
    const herkunft = document.createElement("span");
    herkunft.className = "zugangswerte-herkunft";
    herkunft.textContent = n.zweck ? `${n.zweck}, von ${n.herkunft}` : `von ${n.herkunft}`;
    li.append(name, herkunft);
    liste.appendChild(li);
  }
  el.appendChild(liste);
  const regel = absatz(`Verschlüsselt, nur für dein Konto. Ein vergebener Name wird nie ersetzt. Prüfwert ${kurz}.`);
  regel.classList.add("zugangswerte-regel");
  el.appendChild(regel);
  const viele = namen.length > 1;
  haengeKnopfReiheAn(el, {
    knoepfe: [
      {
        text: viele ? `Alle ${namen.length} ablegen` : "Ablegen",
        primaer: true,
        ja: true,
        nachricht: zugangswerteKnopfJa(id, kurz),
        name: `Zugangswerte ablegen: ${namen.map((n) => n.name).join(", ")}`,
        gesendet: "Gesendet — die Werte werden abgelegt.",
      },
      { text: "Verwerfen", ja: false, nachricht: zugangswerteKnopfNein(id), name: "Zugangswerte verwerfen", gesendet: "Gesendet — nichts wird abgelegt." },
    ],
    bisMs: fristAus(r.gueltig_bis_ms),
    abgelaufenSatz: ABGELAUFEN_SATZ,
  });
  return el;
}
